import { Injectable, signal, computed } from '@angular/core';
import { StorageKey } from '../enums/storageKey';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { lastValueFrom, map, Observable, of, throwError } from 'rxjs';
import { UserTokenRequest } from '../interfaces/auth/IUserTokenRequest';
import { UserTokenResponse } from '../interfaces/auth/IUserTokenResponse';
import { UserRefreshTokenRequest } from '../interfaces/auth/IUserRefreshTokenRequest';
import { Router } from '@angular/router';
import { UserRole } from '../enums/userRole';
import { ResetPasswordRequest } from '../interfaces/auth/IResetPasswordRequest';
import { WebsocketService } from './websocket.service';
import { NavigationService } from './navigation.service';
import { CreateUserRequest } from '../interfaces/auth/ICreateUserRequest';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { UserEmployee } from '../interfaces/user/IUser';
import { RegistrationEducator } from '../interfaces/educator/IRegistrationEducator';
import { CreateUserResponse } from '../interfaces/auth/ICreateUserResponse';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refreshTimeout: any;
  private impersonationTimeout: any;
  private readonly _token = signal<string | null>(localStorage.getItem(StorageKey.AccessToken) || null);
  private readonly _selectedCompany = signal<UserEmployee | null>(this.getSelectedCompanyFromStorage());
  private readonly _availableCompanies = signal<UserEmployee[]>([]);
  private readonly _isImpersonating = signal<boolean>(localStorage.getItem(StorageKey.IsImpersonating) === 'true');

  readonly userRoles = computed(() => this.decodeToken(this._token() ?? '').roles);
  readonly availableCompanies = computed(() => this._availableCompanies());
  readonly selectedCompany = computed(() => this._selectedCompany());
  readonly isImpersonating = computed(() => this._isImpersonating());
  readonly isAuthenticated = computed(
    () => this._token() !== null && this.decodeToken(this._token() ?? '').exp * 1000 > Date.now()
  );

  constructor(
    private http: HttpClient,
    private router: Router,
    private websocketService: WebsocketService,
    private navigationService: NavigationService
  ) {}

  get token() {
    return this._token();
  }

  login(input: UserTokenRequest) {
    return this.http.post<UserTokenResponse>(`${environment.apiUrl}/auth/token`, input).pipe(
      map(result => {
        if (result && result.accessToken) {
          this.setSession(result);
          this.websocketService.connect();
          this.navigationService.clearStack();
        }
        return result;
      })
    );
  }

  verifyRefreshToken(refreshToken: string | null) {
    if (!refreshToken) {
      this.logout();
      return;
    }

    const refreshTokenExp = this.decodeRefreshToken(refreshToken).exp;

    const refreshTokenExpired = refreshTokenExp * 1000 < Date.now();

    if (refreshTokenExpired) {
      this.logout();
      return;
    }
  }

  register(input: CreateUserRequest): Observable<CreateUserResponse> {
    return this.http.post<CreateUserResponse>(`${environment.apiUrl}/user/create`, input);
  }

  getRegistrationEducators(): Observable<RegistrationEducator[]> {
    return this.http.get<RegistrationEducator[]>(`${environment.apiUrl}/user/registration-educators`);
  }

  refreshToken(data: UserRefreshTokenRequest): Observable<UserTokenResponse> {
    return this.http.post<UserTokenResponse>(`${environment.apiUrl}/auth/refresh-token`, data);
  }

  logout(redirect: boolean = true) {
    this._token.set(null);
    this._selectedCompany.set(null);
    this._isImpersonating.set(false);
    localStorage.removeItem(StorageKey.AccessToken);
    localStorage.removeItem(StorageKey.RefreshToken);
    localStorage.removeItem(StorageKey.CurrentUser);
    localStorage.removeItem(StorageKey.SelectedCompany);
    localStorage.removeItem(StorageKey.IsImpersonating);
    this.navigationService.clearStack();

    clearTimeout(this.refreshTimeout);
    clearTimeout(this.impersonationTimeout);
    this.websocketService.disconnect();

    if (redirect) {
      this.router.navigate(['/login']);
    }
  }

  forgotPassword(email: string) {
    const encodedEmail = encodeURIComponent(email);
    return this.http.get<UserTokenResponse>(`${environment.apiUrl}/user/request-password-reset?email=${encodedEmail}`);
  }

  resetPassword(body: ResetPasswordRequest) {
    return this.http.patch<UserTokenResponse>(`${environment.apiUrl}/user/reset-password`, body);
  }

  verifyEmail(code: string) {
    return this.http.get<void>(`${environment.apiUrl}/user/verify-email?code=${code}`);
  }

  restoreSessionFromStorage() {
    const accessToken = localStorage.getItem(StorageKey.AccessToken);
    const refreshToken = localStorage.getItem(StorageKey.RefreshToken);
    const isImpersonating = localStorage.getItem(StorageKey.IsImpersonating) === 'true';

    if (isImpersonating && accessToken) {
      // For impersonation sessions, just verify the token is not already expired
      const decoded = this.decodeToken(accessToken);
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        this.logout();
        return;
      }

      // Restart the 60-minute forced logout timer
      const expiryTime = decoded.exp * 1000;
      const remainingTime = expiryTime - Date.now();

      clearTimeout(this.impersonationTimeout);
      this.impersonationTimeout = setTimeout(() => {
        this.logout();
      }, remainingTime);
    } else if (refreshToken) {
      this.verifyRefreshToken(refreshToken);

      if (accessToken) {
        this.startTokenRefreshTimer(accessToken);
      }
    }
  }

  hasRole(role: UserRole): boolean {
    return this.userRoles().includes(role);
  }

  usernameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null; // allow empty value

    const valid = /^[a-zA-Z0-9_-]+$/.test(value);
    return valid && value.trim().length > 3 ? null : { invalidUsername: true };
  }

  private setSession(tokenResponse: UserTokenResponse) {
    localStorage.setItem(StorageKey.AccessToken, tokenResponse.accessToken);
    localStorage.setItem(StorageKey.RefreshToken, tokenResponse.refreshToken);
    this._token.set(tokenResponse.accessToken);
    this.startTokenRefreshTimer(tokenResponse.accessToken);
  }

  private startTokenRefreshTimer(accessToken: string) {
    const { exp } = this.decodeToken(accessToken);
    const expiresAt = exp * 1000;
    const refreshAt = expiresAt - 60_000; // 1 minute before expiring

    const delay = refreshAt - Date.now();
    clearTimeout(this.refreshTimeout);

    this.refreshTimeout = setTimeout(() => {
      const refreshToken = localStorage.getItem(StorageKey.RefreshToken);
      if (!refreshToken) {
        this.logout();
        return;
      }

      this.refreshToken({ refreshToken }).subscribe({
        next: response => this.setSession(response),
        error: () => this.logout()
      });
    }, delay);
  }

  private decodeToken(token: string): { exp: number; roles: string[]; employeeId: string; employeeIds: string[] } {
    if (!token) {
      return { exp: 0, roles: [], employeeId: '', employeeIds: [] };
    }

    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));

    const roles: string[] = decoded.realm_access?.roles || [];
    const employeeId: string = decoded.employee_id || '';
    const employeeIds: string[] = decoded.employee_ids || [];

    return { exp: decoded.exp, roles, employeeId, employeeIds };
  }

  private decodeRefreshToken(token: string): { exp: number } {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return { exp: decoded.exp };
  }

  switchCompany(company: UserEmployee): Observable<UserTokenResponse> {
    // Get the current refresh token
    const refreshToken = localStorage.getItem(StorageKey.RefreshToken);
    if (!refreshToken) {
      console.error('No refresh token found for company switching');
      return throwError(() => new Error('No refresh token found for company switching'));
    }

    // Use refresh token with the selected employeeId
    return this.refreshToken({
      refreshToken: refreshToken,
      employeeId: company.id
    }).pipe(
      map(result => {
        if (result && result.accessToken) {
          this.setSession(result);
          this.setSelectedCompany(company);
        }
        return result;
      })
    );
  }

  private setSelectedCompany(company: UserEmployee) {
    this._selectedCompany.set(company);
    localStorage.setItem(StorageKey.SelectedCompany, JSON.stringify(company));
  }

  private getSelectedCompanyFromStorage(): UserEmployee | null {
    const stored = localStorage.getItem(StorageKey.SelectedCompany);
    return stored ? JSON.parse(stored) : null;
  }

  setAvailableCompaniesFromUser(userEmployees: UserEmployee[]) {
    this._availableCompanies.set(userEmployees);

    // Set the currently active company
    const activeCompany = userEmployees.find(company => company.isCurrent);
    if (activeCompany) {
      this.setSelectedCompany(activeCompany);
    }
  }

  impersonate(accessToken: string) {
    // Validate token by decoding and checking expiry
    const decoded = this.decodeToken(accessToken);
    const isExpired = decoded.exp * 1000 < Date.now();

    if (isExpired || !decoded.exp) {
      return throwError(() => new Error('Invalid or expired impersonation token'));
    }

    // Set impersonation session (access token only, no refresh token)
    localStorage.setItem(StorageKey.AccessToken, accessToken);
    localStorage.setItem(StorageKey.IsImpersonating, 'true');
    this._token.set(accessToken);
    this._isImpersonating.set(true);

    // Force logout when token expires (calculated from token expiry time)
    const expiryTime = decoded.exp * 1000;
    const remainingTime = expiryTime - Date.now();

    clearTimeout(this.impersonationTimeout);
    this.impersonationTimeout = setTimeout(() => {
      this.logout();
    }, remainingTime);

    this.websocketService.connect();
    this.navigationService.clearStack();

    return of(true);
  }
}
